<template>
  <CustomDropdown
    ref="assignOwnerDropdown"
    maxHeight="400px"
    gap="10px"
    position="start"
    dropdownCSS="p-0"
  >
    <template #trigger>
      <div class="secondary_small gap-1">
        <ThemeImage
          class="size-4"
          :icon="imageNames.ASSIGN_OWNER_GREY"
          alt="assign_owner_icon"
        />
        Assign Owner
        <ThemeImage
          class="size-4"
          :icon="imageNames.DROPDOWN_GREY"
          alt="dropdown_icon"
        />
      </div>
    </template>

    <template #default>
      <div class="flex flex-col w-60">
        <div class="flex flex-col py-3 px-2 gap-2">
          <span class="text-[11px] opacity-60 text_color p-2 font-semibold">
            SELECT AN OWNER
          </span>

          <CustomInput
            format="text"
            v-model="searchQuery"
            placeholder="Search owners"
            type="primary"
            :icon="imageNames.SEARCH_VARIANT1"
            size="size-4"
            alt="search_icon"
          />
        </div>

        <div
          class="flex w-full border_top border_bottom py-3 px-2 justify-center"
        >
          <div
            v-if="filteredOwners.length"
            class="flex flex-col gap-1 overflow-y-auto w-full min-h-36"
            style="max-height: calc(100vh - 772px)"
          >
            <a-tooltip
              v-for="(owner, index) in filteredOwners"
              :key="`${owner.id}_owner_${index}`"
              placement="top"
              :title="
                shouldShowTooltip(owner.id + '_' + index)
                  ? owner?.name || ''
                  : ''
              "
            >
              <div
                class="dropdown_item cursor-pointer min-h-10"
                :class="{ selected: selectedOwner?.id === owner.id }"
                @click="handleOwnerSelection(owner)"
              >
                <ThemeImage
                  class="size-4"
                  :class="{
                    visible: selectedOwner?.id === owner.id,
                    invisible: selectedOwner?.id !== owner.id,
                  }"
                  :icon="imageNames.TICK_GREY"
                  alt="tick_icon"
                />

                <div class="flex flex-row items-center gap-2">
                  <ProfileIcon :data="getOwnerData(owner)" />
                  <div
                    :ref="(el) => setContainerRef(el, owner.id + '_' + index)"
                    class="truncate"
                  >
                    <span
                      :ref="(el) => setTextRef(el, owner.id + '_' + index)"
                      class="text-sm truncate"
                      >{{ owner?.name }}</span
                    >
                  </div>
                </div>
              </div>
            </a-tooltip>
          </div>
          <EmptyScreen v-else padding="py-2" source="assign_owner_dropdown" />
        </div>

        <div class="flex justify-end p-[10px]">
          <a-button
            class="primary_small"
            @click="toChangeEntityOwner"
            :loading="isAssigning"
            :disabled="!selectedOwner"
            >Assign</a-button
          >
        </div>
      </div>
    </template>
  </CustomDropdown>
</template>

<script setup>
import { onMounted, ref, computed } from "vue";
import { useStore } from "vuex";

import DashboardService from "@/src/api/DashboardService.js";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import { message } from "ant-design-vue";
import { formatEntityTypeText } from "@/src/utils/utils.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { useTruncationTooltip } from "@/src/composables/useTruncationTooltip.js";

const props = defineProps({
  source: { type: String, required: true },
  selection: { type: Object, required: true },
  selectCount: { type: Number, required: true },
  viewId: { type: Number, default: null },
  listId: { type: Number, default: null },
  searchQuery: { type: String, default: "" },
});

const emit = defineEmits(["update:success"]);

const store = useStore();
const storeUsers = computed(() => store.getters["users/users"] || []);

const getOwnerData = (owner) => {
  if (!owner?.id) return owner;
  const found = storeUsers.value.find((u) => u.id === owner.id);
  return found || owner;
};

const { setTextRef, setContainerRef, shouldShowTooltip } =
  useTruncationTooltip();

const searchQuery = ref("");
const selectedOwner = ref(null);
const isAssigning = ref(false);
const ownerList = ref(null);
const assignOwnerDropdown = ref({});

const entityMapping = {
  contact: "contacts",
  company: "companies",
  deal: "deals",
  lead: "leads",
  task: "tasks",
};

const filteredOwners = computed(() => {
  if (!ownerList.value) return [];

  const query = searchQuery.value.toLowerCase().trim();

  if (!query) return ownerList.value;

  return ownerList.value.filter((owner) => {
    const ownerName = owner.name?.toLowerCase() || "";
    const ownerEmail = owner.email?.toLowerCase() || "";

    return ownerName.includes(query) || ownerEmail.includes(query);
  });
});

const entityTextWithCount = computed(() =>
  formatEntityTypeText({
    entityType: props.source,
    count: props.selectCount,
    includeCount: true,
    titleCase: true,
  }),
);

const handleOwnerSelection = (owner) => {
  selectedOwner.value = selectedOwner.value?.id === owner.id ? null : owner;
};

const toFetchUsers = async () => {
  try {
    const response = await DashboardService.fetchUsers();
    if (response?.success) {
      const users = response?.users || [];
      ownerList.value = Array.from(
        new Map(users.map((user) => [user.id, user])).values(),
      );
    }
  } catch (error) {
    console.error("Error fetching users:", error);
  }
};

const toChangeEntityOwner = async () => {
  if (!selectedOwner.value) return;

  try {
    isAssigning.value = true;

    const payload = {
      owner_id: selectedOwner.value?.id,
      select_all: props.selection?.select_all,
      selected_ids: props.selection?.selected_ids,
      unselected_ids: props.selection?.unselected_ids,
    };

    if (props.viewId) {
      payload.view_id = props.viewId;
    }

    if (props?.listId) {
      payload.list_id = props.listId;
    }

    if (props.searchQuery && props.searchQuery.trim() !== "") {
      payload.search_query = props.searchQuery;
    }

    const response = await DashboardService.changeEntityOwner(
      entityMapping[props.source],
      payload,
    );

    if (response?.success) {
      message.success(
        `${entityTextWithCount.value} assigned to ${selectedOwner.value?.name} successfully!`,
      );
      // Capture owner data before clearing
      const assignedOwnerData = {
        id: selectedOwner.value?.id,
        name: selectedOwner.value?.name,
      };
      selectedOwner.value = null;
      searchQuery.value = "";
      assignOwnerDropdown.value?.closeDropdown();
      emit("update:success", {
        assignedOwner: assignedOwnerData,
        selection: props.selection,
      });
    } else {
      message.error("Failed to change entity owner");
    }
  } catch (error) {
    message.error("An error occurred while changing entity owner");
    console.error("An error occurred while changing entity owner: ", error);
  } finally {
    isAssigning.value = false;
  }
};

onMounted(() => {
  toFetchUsers();
});
</script>

<style scoped>
.selected {
  background-color: var(--white-opacity-6);
}
</style>
